import { SidebarHeader } from '@/components/ui/sidebar';
import { Button } from '@/components/ui/button';
import { useToast } from '@/components/ui/use-toast';
import { useChapterBeats, useCreateChapterBeat, useUpdateChapterBeat, useDeleteChapterBeat } from '@/hooks/useChapterBeats';
import { cn } from '@/lib/utils';
import { Clapperboard, Plus, ChevronRight, Sparkles, Loader2 } from 'lucide-react';
import { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { useParams } from 'react-router-dom';
import { useChapter } from '@/hooks/useChapters';
import { useProjectMeta } from '@/hooks/meta/useProjectMeta';
import { aiChapterBeatsService } from '@/services/ai-service';
import { useLicense } from '@/hooks/useLicense';
import { SceneItem, type DraftBeat } from './scene-item';

interface ChapterSceneBuilderTabProps {
  chapterId?: string | number;
}

async function parseAIStreamResponse(response: Response) {
  const reader = response.body?.getReader();
  if (!reader) throw new Error('No reader available');

  const decoder = new TextDecoder();
  let buffer = '';
  while (true) {
    const { done, value } = await reader.read();
    if (done) break;
    buffer += decoder.decode(value, { stream: true });
  }

  // Final parse matching the plan-scenes-step logic
  try {
    let jsonStr = buffer.trim();
    const firstCurly = jsonStr.indexOf('{');
    const lastCurly = jsonStr.lastIndexOf('}');
    
    if (firstCurly !== -1 && lastCurly !== -1) {
      jsonStr = jsonStr.substring(firstCurly, lastCurly + 1);
    }
    return JSON.parse(jsonStr || buffer);
  } catch (parseError) {
    console.warn('Failed initial parse, attempting Vercel protocol extraction...');
    let aggregatedText = '';
    const lines = buffer.split('\n');
    for (const line of lines) {
      if (line.startsWith('0:')) {
        try { aggregatedText += JSON.parse(line.substring(2)); } catch (e) {}
      }
    }
    
    let extractedJson = aggregatedText.trim() ? aggregatedText.trim() : buffer.trim();
    const first = extractedJson.indexOf('{');
    const last = extractedJson.lastIndexOf('}');
    if (first !== -1 && last !== -1) {
      extractedJson = extractedJson.substring(first, last + 1);
    }
    return JSON.parse(extractedJson);
  }
}

export function ChapterSceneBuilderTab({ chapterId }: ChapterSceneBuilderTabProps) {
  const { toast } = useToast();
  const { projectId } = useParams();
  const { data: beats = [], isLoading: isLoadingBeats } = useChapterBeats(chapterId);
  const { data: chapter } = useChapter(chapterId);
  const { currentStoryId } = useProjectMeta(projectId || null);
  
  const createBeatMutation = useCreateChapterBeat();
  const updateBeatMutation = useUpdateChapterBeat();
  const deleteBeatMutation = useDeleteChapterBeat();
  const { hasAIFeatures } = useLicense();

  const [draftBeats, setDraftBeats] = useState<DraftBeat[]>([]);
  const [isGenerating, setIsGenerating] = useState(false);

  const handleAddBeat = async () => {
    if (!chapterId) return;
    
    try {
      await createBeatMutation.mutateAsync({
        chapterId: Number(chapterId),
        title: `Scene ${beats.length + 1}`,
      });
      toast({
        title: "Scene Added",
        description: "A new scene has been added.",
      });
    } catch (error) {
      toast({
        title: "Error",
        description: "Failed to add scene.",
        variant: "destructive",
      });
    }
  };

  const handleSaveDraft = async (tempId: string, title: string, description: string) => {
    if (!chapterId) return;
    try {
      await createBeatMutation.mutateAsync({
        chapterId: Number(chapterId),
        title,
        description
      });
      setDraftBeats(prev => prev.filter(d => d.tempId !== tempId));
      toast({
        title: "Draft Saved",
        description: "The AI-generated scene has been approved and saved.",
      });
    } catch (error) {
      toast({
        title: "Error",
        description: "Failed to save draft.",
        variant: "destructive",
      });
    }
  };

  const handleGenerateNext = async () => {
    if (!chapterId || !projectId || isGenerating) return;
    setIsGenerating(true);

    try {
      const storyId = currentStoryId?.toString();
      const response = await aiChapterBeatsService.streamGenerateBeat(
        Number(chapterId),
        [
          ...beats.map(b => ({ name: b.title, description: b.description || '' })),
          ...draftBeats.map(b => ({ name: b.title, description: b.description || '' }))
        ],
        undefined,
        undefined,
        chapter?.title,
        chapter?.description,
        projectId,
        storyId
      );

      const result = await parseAIStreamResponse(response);

      if (result && result.name) {
        setDraftBeats(prev => [...prev, {
          tempId: crypto.randomUUID(),
          title: result.name,
          description: result.description || '',
          isDraft: true
        }]);
      }
    } catch (error) {
      console.error('Error generating beat:', error);
      toast({
        title: "Error",
        description: "Failed to generate AI scene.",
        variant: "destructive",
      });
    } finally {
      setIsGenerating(false);
    }
  };

  const handleRegenerateBeat = async (id: number | string, orderIndex: number, currentTitle: string, currentDescription: string) => {
    if (!chapterId || !projectId) return null;
    
    try {
      const storyId = currentStoryId?.toString();
      const displayOrder = orderIndex + 1;
      
      const otherBeats = [
        ...beats.filter(b => b.id !== id).map(b => ({ name: b.title, description: b.description || '', displayOrder: beats.indexOf(b) + 1 })),
        ...draftBeats.filter(d => d.tempId !== id).map(d => ({ name: d.title, description: d.description || '', displayOrder: beats.length + draftBeats.indexOf(d) + 1 }))
      ];

      const response = await aiChapterBeatsService.streamGenerateBeat(
        Number(chapterId),
        otherBeats,
        undefined,
        { 
          id: typeof id === 'number' ? id : undefined,
          name: currentTitle, 
          description: currentDescription,
          displayOrder
        },
        chapter?.title,
        chapter?.description,
        projectId,
        storyId
      );
      const result = await parseAIStreamResponse(response);

      if (result && (result.name || result.title)) {
        return { 
          title: result.name || result.title, 
          description: result.description || '' 
        };
      }
    } catch (error) {
      console.error('Error regenerating beat:', error);
      toast({
        title: "Regeneration Failed",
        description: "AI failed to brainstorm a replacement for this scene.",
        variant: "destructive",
      });
    }
    return null;
  };

  const handleUpdateBeat = async (beatId: number, updates: any) => {
    if (!chapterId) return;
    try {
      await updateBeatMutation.mutateAsync({
        beatId,
        updates,
        chapterId: Number(chapterId)
      });
    } catch (error) {
      console.error('Failed to update beat:', error);
    }
  };

  const handleDeleteBeat = async (id: number | string) => {
    if (!chapterId) return;
    
    // Check if it's a draft
    if (typeof id === 'string') {
      setDraftBeats(prev => prev.filter(d => d.tempId !== id));
      return;
    }

    try {
      await deleteBeatMutation.mutateAsync({
        beatId: id,
        chapterId: Number(chapterId)
      });
    } catch (error) {
      toast({
        title: "Error",
        description: "Failed to remove scene.",
        variant: "destructive",
      });
    }
  };

  const renderContent = () => {
    if (isLoadingBeats) {
      return (
        <div className="flex-1 flex items-center justify-center p-8">
          <Loader2 className="h-4 w-4 animate-spin text-muted-foreground/40" />
        </div>
      );
    }

    if (beats.length === 0) {
      return (
        <div className="flex-1 flex flex-col items-center justify-center p-8 text-center text-muted-foreground min-h-0">
          <div className="w-16 h-16 rounded-[2rem] bg-muted/30 flex items-center justify-center mb-6 ring-1 ring-border/40 backdrop-blur-sm">
            <Clapperboard className="h-8 w-8 text-muted-foreground/20" />
          </div>
          <h3 className="text-sm font-semibold text-foreground/80 mb-2 tracking-tight">Deconstruct your Story</h3>
          <p className="text-xs max-w-[200px] leading-relaxed mb-6 opacity-60">
            Break your chapter into manageable scenes to maintain pacing and focus.
          </p>
          <Button 
            onClick={handleAddBeat}
            className="rounded-xl shadow-lg shadow-primary/20 bg-primary h-10 px-6 font-medium active:scale-95 transition-all gap-2"
          >
            <Plus className="h-4 w-4" />
            New Scene
          </Button>
        </div>
      );
    }

    return (
      <div className="flex-1 w-full overflow-y-auto custom-scrollbar p-0 space-y-0 min-h-0 relative">
        <div className="p-4 space-y-6 pb-4 relative">
          {/* Timeline Connector Line - Adjusted for new alignment */}
          <div className="absolute left-[33.5px] top-4 bottom-0 w-[1px] bg-gradient-to-b from-border/80 via-border/40 to-transparent pointer-events-none" />
          
          <AnimatePresence mode="popLayout">
            {beats.map((beat, index) => (
              <SceneItem 
                key={beat.id}
                beat={beat}
                index={index}
                onUpdate={handleUpdateBeat}
                onDelete={handleDeleteBeat}
                onRegenerate={handleRegenerateBeat}
              />
            ))}
            {draftBeats.map((draft, index) => (
              <SceneItem 
                key={draft.tempId}
                beat={draft}
                index={beats.length + index}
                onDelete={handleDeleteBeat}
                onSaveDraft={handleSaveDraft}
                onRegenerate={handleRegenerateBeat}
              />
            ))}
          </AnimatePresence>

          {/* Add Scene Trigger as a Card-aligned Button */}
          <motion.div layout className="pl-[52px]">
            <Button
              onClick={handleAddBeat}
              variant="ghost"
              disabled={isGenerating}
              className="w-full h-10 rounded-xl border border-dashed border-border/60 hover:border-primary/40 hover:bg-primary/5 text-muted-foreground hover:text-primary transition-all duration-300 gap-2 font-medium text-xs group py-0 justify-center"
            >
              <Plus className="h-3.5 w-3.5 p-0.5 rounded-full bg-muted-foreground/10 group-hover:bg-primary/20" />
              Add Next Scene
            </Button>
          </motion.div>
        </div>
      </div>
    );
  };

  return (
    <div className="flex flex-col h-full w-full bg-background overflow-hidden border-l border-border/60">
      <SidebarHeader className="h-12 border-b border-border/80 px-4 py-0 shrink-0 bg-background/50 backdrop-blur-xl z-20 flex flex-row items-center justify-between">
        <div className="flex items-center gap-2.5">
          <div className="p-1.5 rounded-lg bg-primary/10 border border-primary/20">
            <Clapperboard className="h-3.5 w-3.5 text-primary" />
          </div>
          <span className="text-[13px] font-bold tracking-tight bg-gradient-to-b from-foreground to-foreground/70 bg-clip-text text-transparent">
            Scene Builder
          </span>
        </div>
      </SidebarHeader>

      <div className="flex-1 flex flex-col min-h-0 relative overflow-hidden">
        {renderContent()}
        
        {/* Fixed Footer for AI Button - only for AI-enabled plans */}
        {hasAIFeatures && beats.length > 0 && (
          <div className="flex-shrink-0 z-10 border-t border-border/80 bg-background/80 backdrop-blur-xl">
            <div className="p-4 pt-4 pb-5">
              <Button
                variant="ghost"
                onClick={handleGenerateNext}
                disabled={isGenerating}
                className={cn(
                  "w-full h-11 rounded-xl bg-muted/60 hover:bg-muted text-foreground/80 hover:text-foreground font-semibold text-[13px] transition-all active:scale-[0.98] group relative overflow-hidden px-4",
                  "border border-border/80 hover:border-primary/30 shadow-sm hover:shadow-md hover:-translate-y-0.5",
                  isGenerating && "opacity-80"
                )}
              >
                {/* Refined Shimmer Effect */}
                <div className="absolute inset-0 w-[200%] h-full bg-gradient-to-r from-transparent via-primary/[0.05] to-transparent -translate-x-[100%] group-hover:animate-[shimmer_2.5s_infinite] pointer-events-none" />
                
                <div className="flex items-center justify-between w-full relative z-10">
                  <div className="flex items-center gap-2.5">
                    <div className="relative">
                      {isGenerating ? (
                        <Loader2 className="h-4 w-4 text-primary animate-spin" />
                      ) : (
                        <>
                          <Sparkles className="h-4 w-4 text-primary/60 group-hover:text-amber-500 group-hover:scale-110 transition-all duration-300" />
                          <div className="absolute inset-0 bg-amber-400/20 blur-lg opacity-0 group-hover:opacity-100 transition-opacity duration-500" />
                        </>
                      )}
                    </div>
                    <span className="tracking-tight text-foreground/90 group-hover:text-foreground">
                      {isGenerating ? 'AI is brainstorming...' : 'Generate next with AI chat'}
                    </span>
                  </div>
                  {!isGenerating && <ChevronRight className="h-3.5 w-3.5 opacity-40 group-hover:translate-x-1 group-hover:opacity-100 transition-all duration-300" />}
                </div>
              </Button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
